import * as React from 'react';
import { Link } from 'react-router-dom';
import { Control, LocalForm, Errors } from 'react-redux-form';
import ReactTable, { Column } from 'react-table';
import { hasValue, getDefaultErrorMessages } from '@common/validators';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { Role, Roles } from '@models/Roles';
import { TooltipComponent } from '@common/components/tooltip/tooltip.component';
import { ContractSearchViewModel } from '@models/ServiceWorkOrder';
import { dateParser } from '@common/dateParser';

export interface ContractSearchComponentProperties {
  readonly searchContracts: (
    pageNumber: number,
    pageSize: number,
    query: string | null,
    orderBy: string,
    orderDirection: string
  ) => void;
  readonly message: string;
  readonly searchQuery: string | null;
  readonly searchLoading: boolean;
  readonly searchPageNumber: number;
  readonly searchPageSize: number;
  readonly searchPageCount: number;
  readonly searchResults: ContractSearchViewModel[];
  readonly searchResultsTotalCount: number;
}

interface FormModel {
  readonly query: string;
}

// Component definition
export class ContractSearchComponent extends React.Component<ContractSearchComponentProperties> {
  isInitialized: boolean = false;
  renderError() {
    if (!this.props.message) {
      return <div />;
    }
    return <div className="error">{this.props.message.toString()}</div>;
  }

  render() {
    const columns: Column[] = [
      {
        accessor: (d: ContractSearchViewModel) =>
          d && (
            <Link
              to={{
                pathname: getRoute(RouteDefinitions.contract.Details, {
                  id: d.id
                }),
                state: { viewOnly: true }
              }}
              target="_blank"
            >
              {d.number}
            </Link>
          ),
        id: 'number',
        Header: 'Contract Number',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (d: ContractSearchViewModel) =>
          d.address
            ? d.address.line1 +
              ', ' +
              (d.address.line2 ? d.address.line2 + ', ' : '') +
              d.address.city +
              ', ' +
              d.address.state.abbreviation +
              '  ' +
              d.address.zip
            : '',
        id: 'address',
        Header: 'Property Address',
        minWidth: 200,
        sortable: false
      },
      {
        accessor: (d: ContractSearchViewModel) =>
          d &&
          d.buyer && (
            <Link
              to={{
                pathname: getRoute(RouteDefinitions.customer.Details, {
                  id: d.buyer!.id
                }),
                state: { viewOnly: true }
              }}
              target="_blank"
            >
              {d.buyer.name}
            </Link>
          ),
        id: 'buyer.id',
        Header: 'Buyer',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (d: ContractSearchViewModel) =>
          d &&
          d.seller && (
            <Link
              to={{
                pathname: getRoute(RouteDefinitions.customer.Details, {
                  id: d.seller!.id
                }),
                state: { viewOnly: true }
              }}
              target="_blank"
            >
              {d.seller!.name}
            </Link>
          ),
        id: 'seller.id',
        Header: 'Seller',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (d: ContractSearchViewModel) =>
          d.startDate ? dateParser.parse(d.startDate).format('MM/DD/YYYY') : '',
        id: 'startDate',
        Header: 'Start Date',
        sortable: true,
        maxWidth: 150,
        defaultSortDesc: true
      },
      {
        accessor: (d: ContractSearchViewModel) => (d.endDate ? dateParser.parse(d.endDate).format('MM/DD/YYYY') : ''),
        id: 'endDate',
        Header: 'End Date',
        maxWidth: 150,
        sortable: true,
        defaultSortDesc: true
      }
    ];

    return (
      <div className="pb-10">
        <LocalForm
          className="form-horizontal"
          hideNativeErrors={true}
          onSubmit={(props: FormModel) => this.handleSearch(props.query)}
        >
          <div className="row">
            <div className="col-md-12">
              <h2>Contracts</h2>
              <div className="text-right">
                <Link className="btn btn-primary" to={getRoute(RouteDefinitions.customer.Search)}>
                  Customer Search
                </Link>
                <Link className="btn btn-primary" to={getRoute(RouteDefinitions.serviceworkorder.Search)}>
                  SWO Search
                </Link>
              </div>
            </div>
            <div className="form-control col-md-12">
              <label htmlFor=".query">Search</label>
              <TooltipComponent
                text="Search by Id, Contract Number, Property Address, Customer Name, Customer Address"
                side="top"
              />
              <Control.text model=".query" validators={{ required: hasValue }} />
              <Errors
                className="error"
                model=".query"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
              {this.props.searchPageCount * 10 > 10000 && (
                <span> Your search is too broad. Narrow the search with more specific terms. </span>
              )}
            </div>
            <div className="col-md-12">
              <div className="row center-xs text-right">
                <button className="btn btn-primary" disabled={this.props.searchLoading} type="submit">
                  Search
                </button>
              </div>
            </div>
          </div>
        </LocalForm>
        {/* <div className="row">
                    <div className="col-xs-12">
                        <h2>Results Found: {this.props.searchResultsTotalCount || '0'}</h2>
                    </div>
                </div> */}
        <ReactTable
          onPageChange={() => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          className="-striped -highlight"
          columns={columns}
          data={this.props.searchResults}
          defaultPageSize={10}
          loading={this.props.searchLoading}
          manual={true}
          onFetchData={this.handleFetchData}
          pages={this.props.searchPageCount}
          previousText="Prev"
        />
        {Role.CheckRoles([Roles.Admin, ...Role.GetAllIBS(), ...Role.GetAllService(), ...Role.GetAllAccounting()]) && (
          <Link className="btn btn-primary" to={RouteDefinitions.contract.Create}>
            Create New
          </Link>
        )}
      </div>
    );
  }

  private handleFetchData = (state: {
    page: number;
    pageSize: number;
    sorted: Array<{ id: string; desc: boolean }>;
  }): void => {
    const orderBy = state.sorted[0] ? state.sorted[0].id : 'id';
    const orderDirection = state.sorted[0] ? (state.sorted[0].desc ? 'desc' : 'asc') : 'asc';

    if (this.isInitialized) {
      this.props.searchContracts(state.page + 1, state.pageSize, this.props.searchQuery, orderBy, orderDirection);
    }
    this.isInitialized = true;
  };

  private handleSearch = (query: string): void => {
    this.isInitialized = true;
    this.props.searchContracts(1, this.props.searchPageSize, query, 'id', 'asc');
  };
}



// WEBPACK FOOTER //
// ./src/contract/_components/contract-search/contract-search.component.tsx